import { useMap } from "@vis.gl/react-maplibre";
import type { MapMouseEvent } from "maplibre-gl";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { getAliveMap } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  appLanguageSelector,
  createErrorResult,
  errors,
  isControlClick,
  PassengerMapControlsEnum,
  passengersMapAddStationSearchValueAtom,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { usePassengerMapSelectedControl } from "../../hooks";

/**
 * Turns a click on the map into an address, but only while the add-one-station panel is open — the same
 * gate the Leaflet version had.
 */
export const MapClickEvent: FC = () => {
  const { current: mapRef } = useMap();
  const { addNewStationControl } = usePassengerMapSelectedControl();
  const { searchAddressByGeo } = useGoogleServices();
  const language = useRecoilValue(appLanguageSelector);
  const setSearchValue = useSetRecoilState(
    passengersMapAddStationSearchValueAtom,
  );
  const { t } = useTranslation();

  const handleLocationClicked = useEvent(
    async (lat: number, lng: number): Promise<void> => {
      try {
        const placeResult = await searchAddressByGeo({
          geoParams: { location: { lat, lng }, language },
        });

        if (!placeResult.success) {
          warnAndToastSimpleResult(placeResult, t);
          return;
        }

        setSearchValue({
          fullAddress: placeResult.data?.text?.text ?? "",
          latitude: lat,
          longitude: lng,
        });
      } catch {
        warnAndToastSimpleResult(
          createErrorResult(
            errors.common.technicalProblemOccurred,
            "Error while changing location",
          ),
          t,
        );
      }
    },
  );

  const handleMapClick = useEvent((event: MapMouseEvent): void => {
    // The control cluster is drawn over the canvas, so a click on it also reaches the map.
    if (isControlClick(event)) {
      return;
    }

    if (
      addNewStationControl.selectedPanel !==
      PassengerMapControlsEnum.addOneNewStation
    ) {
      return;
    }

    const { lat, lng } = event.lngLat ?? {};

    if (!lat || !lng) {
      return;
    }

    void handleLocationClicked(lat, lng);
  });

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map) {
      return;
    }

    map.on("click", handleMapClick);

    return () => {
      getAliveMap(mapRef)?.off("click", handleMapClick);
    };
  }, [mapRef, handleMapClick]);

  return null;
};
